import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { api } from "@/lib/apiClient";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from "recharts";
import { TrendingUp, TrendingDown, DollarSign, Receipt, CreditCard, AlertCircle } from "lucide-react";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "@/components/ui/chart";
import { KpiCard } from "./components/KpiCard";
import { ChartCard } from "./components/ChartCard";
import { DateRangeFilter, DEFAULT_DATE_RANGE, type DateRange } from "./components/DateRangeFilter";
import { ProjectDrilldownTable } from "./components/ProjectDrilldownTable";
import { UnassignedRevenuesTable } from "./components/UnassignedRevenuesTable";
import { formatCurrency, formatCompactCurrency, formatNumber } from "@/lib/formatting";
import { Button } from "@/components/ui/button";
import type { Revenue, Cost, Project, CostMpkAllocation } from "@shared/schema";

/**
 * Formats a date range for API query parameters.
 * Returns ISO format (YYYY-MM-DD) strings.
 */
function formatDateRangeForApi(dateRange: DateRange) {
  return {
    from: format(dateRange.from, "yyyy-MM-dd"),
    to: format(dateRange.to, "yyyy-MM-dd"),
  };
}

const MONTHS_PL = [
  "Sty", "Lut", "Mar", "Kwi", "Maj", "Cze",
  "Lip", "Sie", "Wrz", "Paź", "Lis", "Gru"
];

const CHART_COLORS = [
  "hsl(220, 70%, 50%)",   // Blue
  "hsl(160, 60%, 45%)",   // Green
  "hsl(30, 80%, 55%)",    // Orange
  "hsl(280, 65%, 60%)",   // Purple
  "hsl(340, 75%, 55%)",   // Pink
  "hsl(45, 90%, 50%)",    // Yellow
  "hsl(190, 70%, 45%)",   // Cyan
  "hsl(0, 70%, 55%)",     // Red
];

function parsePolishDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null;

  // Try DD.MM.YYYY format (Polish)
  if (dateStr.includes(".")) {
    const parts = dateStr.split(".");
    if (parts.length !== 3) return null;
    const [day, month, year] = parts.map(Number);
    return new Date(year, month - 1, day);
  }
  // Try YYYY-MM-DD format (ISO)
  if (dateStr.includes("-")) {
    const parts = dateStr.split("-");
    if (parts.length !== 3) return null;
    const [year, month, day] = parts.map(Number);
    return new Date(year, month - 1, day);
  }

  return null;
}

const ITEMS_PER_PAGE = 10;

export default function RevenueAnalytics() {
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const [selectedProjectType, setSelectedProjectType] = useState<string | null>(null);
  const [showUnassignedRevenues, setShowUnassignedRevenues] = useState(false);
  const [drilldownPage, setDrilldownPage] = useState(1);

  // Format date range for API requests
  const apiDateRange = formatDateRangeForApi(dateRange);

  // Fetch revenues filtered by date range (backend filtering)
  const { data: filteredRevenues = [], isLoading: revenuesLoading } = useQuery<Revenue[]>({
    queryKey: ["/api/revenues", { from: apiDateRange.from, to: apiDateRange.to }],
    queryFn: () => {
      const params = new URLSearchParams({
        from: apiDateRange.from,
        to: apiDateRange.to,
        dateField: "dataWystawienia",
      });
      return api.get<Revenue[]>(`/api/revenues?${params}`);
    },
  });

  // Fetch costs filtered by date range (backend filtering)
  const { data: filteredCosts = [], isLoading: costsLoading } = useQuery<Cost[]>({
    queryKey: ["/api/costs", { from: apiDateRange.from, to: apiDateRange.to }],
    queryFn: () => {
      const params = new URLSearchParams({
        from: apiDateRange.from,
        to: apiDateRange.to,
        dateField: "terminPlatnosci",
      });
      return api.get<Cost[]>(`/api/costs?${params}`);
    },
  });

  // Projects don't need date filtering for this analytics page
  const { data: projects = [], isLoading: projectsLoading } = useQuery<Project[]>({
    queryKey: ["/api/projects"],
  });

  const { data: mpkAllocations = [], isLoading: mpkAllocationsLoading } = useQuery<CostMpkAllocation[]>({
    queryKey: ["/api/mpk-allocations"],
  });

  const isLoading = revenuesLoading || costsLoading || projectsLoading || mpkAllocationsLoading;

  // Calculate KPIs
  const kpis = useMemo(() => {
    const totalRevenue = filteredRevenues.reduce((sum, r) => sum + (parseFloat(r.kwotaNetto || "0") || 0), 0);
    const totalCosts = filteredCosts.reduce((sum, c) => sum + (parseFloat(c.netto || "0") || 0), 0);
    const grossProfit = totalRevenue - totalCosts;
    const outstandingAmount = filteredRevenues
      .filter(r => r.czyZaplacono !== "Tak")
      .reduce((sum, r) => sum + (parseFloat(r.kwotaBrutto || "0") || 0), 0);
    const paidAmount = filteredRevenues
      .filter(r => r.czyZaplacono === "Tak")
      .reduce((sum, r) => sum + (parseFloat(r.kwotaBrutto || "0") || 0), 0);

    return {
      totalRevenue,
      totalCosts,
      grossProfit,
      grossMargin: totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0,
      outstandingAmount,
      paidAmount,
      outstandingCount: filteredRevenues.filter(r => r.czyZaplacono !== "Tak").length,
      invoiceCount: filteredRevenues.length,
      costCount: filteredCosts.length,
    };
  }, [filteredRevenues, filteredCosts]);

  // Monthly revenue vs costs
  const monthlyData = useMemo(() => {
    const data: { month: string; przychód: number; koszty: number }[] = MONTHS_PL.map(month => ({
      month,
      przychód: 0,
      koszty: 0,
    }));

    filteredRevenues.forEach(r => {
      const date = parsePolishDate(r.dataWystawienia);
      if (date) {
        const monthIdx = date.getMonth();
        data[monthIdx].przychód += parseFloat(r.kwotaNetto || "0") || 0;
      }
    });

    filteredCosts.forEach(c => {
      const date = parsePolishDate(c.terminPlatnosci);
      if (date) {
        const monthIdx = date.getMonth();
        data[monthIdx].koszty += parseFloat(c.netto || "0") || 0;
      }
    });

    return data;
  }, [filteredRevenues, filteredCosts]);

  // Revenue by project type
  const revenueByProjectType = useMemo(() => {
    const projectTypeMap = new Map<string, string>();
    projects.forEach(p => {
      if (p.nrPorzadkowy && p.typProjektu) {
        projectTypeMap.set(p.nrPorzadkowy, p.typProjektu);
      }
    });

    const typeRevenue: Record<string, number> = {};
    filteredRevenues.forEach(r => {
      const projectType = projectTypeMap.get(r.nrPorzadkowyProjektu || "");
      if (projectType) {
        typeRevenue[projectType] = (typeRevenue[projectType] || 0) + (parseFloat(r.kwotaNetto || "0") || 0);
      }
    });

    return Object.entries(typeRevenue)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [filteredRevenues, projects]);

  // Pie chart data - includes "Nieprzypisane" only when viewing unassigned revenues
  const pieChartData = useMemo(() => {
    if (!showUnassignedRevenues) {
      return revenueByProjectType;
    }
    // Calculate unassigned revenue total
    const projectNrs = new Set(projects.map(p => p.nrPorzadkowy));
    const unassignedTotal = filteredRevenues
      .filter(r => !r.nrPorzadkowyProjektu || !projectNrs.has(r.nrPorzadkowyProjektu))
      .reduce((sum, r) => sum + (parseFloat(r.kwotaNetto || "0") || 0), 0);

    // Add "Nieprzypisane" to the data and re-sort
    return [...revenueByProjectType, { name: "Nieprzypisane", value: unassignedTotal }]
      .sort((a, b) => b.value - a.value);
  }, [revenueByProjectType, showUnassignedRevenues, filteredRevenues, projects]);

  // Unassigned revenues (no project or project not found)
  const unassignedRevenues = useMemo(() => {
    const projectNrs = new Set(projects.map(p => p.nrPorzadkowy));
    return filteredRevenues.filter(r =>
      !r.nrPorzadkowyProjektu || !projectNrs.has(r.nrPorzadkowyProjektu)
    );
  }, [filteredRevenues, projects]);

  // Payment status
  const paymentStatus = useMemo(() => {
    const paid = filteredRevenues.filter(r => r.czyZaplacono === "Tak").length;
    const unpaid = filteredRevenues.filter(r => r.czyZaplacono !== "Tak").length;
    return [
      { name: "Zapłacone", value: paid, color: "hsl(160, 60%, 45%)" },
      { name: "Niezapłacone", value: unpaid, color: "hsl(0, 70%, 55%)" },
    ];
  }, [filteredRevenues]);

  // Revenue trend (cumulative)
  const revenueTrend = useMemo(() => {
    let cumulative = 0;
    return MONTHS_PL.map((month, idx) => {
      const monthRevenue = filteredRevenues
        .filter(r => {
          const date = parsePolishDate(r.dataWystawienia);
          return date && date.getMonth() === idx;
        })
        .reduce((sum, r) => sum + (parseFloat(r.kwotaNetto || "0") || 0), 0);
      cumulative += monthRevenue;
      return { month, skumulowany: cumulative, miesięczny: monthRevenue };
    });
  }, [filteredRevenues]);

  // Drill-down projects by selected type
  const drilldownProjects = useMemo(() => {
    if (!selectedProjectType) return [];
    const projectsWithRevenue = new Set(
      filteredRevenues.map(r => r.nrPorzadkowyProjektu).filter(Boolean)
    );

    // Status priority: W trakcie first, then others
    const statusPriority: Record<string, number> = {
      "W trakcie": 0,
      "W przygotowaniu": 1,
      "Wstrzymany": 2,
      "Zakończony": 3,
      "Administracyjny": 4,
    };

    return projects
      .filter(p =>
        p.typProjektu === selectedProjectType &&
        p.rodzajProjektu === "Zewnętrzny" &&
        projectsWithRevenue.has(p.nrPorzadkowy)
      )
      .sort((a, b) => {
        const aPriority = statusPriority[a.statusProjektu || ""] ?? 5;
        const bPriority = statusPriority[b.statusProjektu || ""] ?? 5;
        if (aPriority !== bPriority) return aPriority - bPriority;
        // Secondary sort by contract value (descending)
        const aVal = parseFloat(a.wartoscUmowyNetto || "0") || 0;
        const bVal = parseFloat(b.wartoscUmowyNetto || "0") || 0;
        return bVal - aVal;
      });
  }, [selectedProjectType, projects, filteredRevenues]);

  // Paginated projects
  const paginatedProjects = useMemo(() => {
    const start = (drilldownPage - 1) * ITEMS_PER_PAGE;
    return drilldownProjects.slice(start, start + ITEMS_PER_PAGE);
  }, [drilldownProjects, drilldownPage]);

  const totalPages = Math.ceil(drilldownProjects.length / ITEMS_PER_PAGE);

  // Paginated unassigned revenues
  const paginatedUnassignedRevenues = useMemo(() => {
    const start = (drilldownPage - 1) * ITEMS_PER_PAGE;
    return unassignedRevenues.slice(start, start + ITEMS_PER_PAGE);
  }, [unassignedRevenues, drilldownPage]);

  const unassignedTotalPages = Math.ceil(unassignedRevenues.length / ITEMS_PER_PAGE);

  // Reset drill-down when date range changes
  useEffect(() => {
    setSelectedProjectType(null);
    setShowUnassignedRevenues(false);
    setDrilldownPage(1);
  }, [dateRange]);

  // Handlers for drill-down
  const handlePieSliceClick = (data: { name: string }) => {
    if (data.name === "Nieprzypisane") {
      setShowUnassignedRevenues(true);
      setSelectedProjectType(null);
    } else {
      setSelectedProjectType(data.name);
      setShowUnassignedRevenues(false);
    }
    setDrilldownPage(1);
  };

  const handleCloseDrilldown = () => {
    setSelectedProjectType(null);
    setShowUnassignedRevenues(false);
    setDrilldownPage(1);
  };

  const chartConfig = {
    przychód: { label: "Przychód", color: "hsl(160, 60%, 45%)" },
    koszty: { label: "Koszty", color: "hsl(0, 70%, 55%)" },
    skumulowany: { label: "Skumulowany", color: "hsl(220, 70%, 50%)" },
    miesięczny: { label: "Miesięczny", color: "hsl(160, 60%, 45%)" },
  };

  if (isLoading) {
    return (
      <div className="h-full overflow-auto p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-64" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-muted rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Analityka - Przychody</h1>
          <p className="text-sm text-muted-foreground">Przegląd finansowy</p>
        </div>
        <DateRangeFilter dateRange={dateRange} onDateRangeChange={setDateRange} />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title="Przychód netto"
          value={formatCompactCurrency(kpis.totalRevenue)}
          subtitle={`${kpis.invoiceCount} faktur`}
          icon={TrendingUp}
        />
        <KpiCard
          title="Koszty netto"
          value={formatCompactCurrency(kpis.totalCosts)}
          subtitle={`${kpis.costCount} faktur`}
          icon={Receipt}
        />
        <KpiCard
          title="Zysk brutto"
          value={formatCompactCurrency(kpis.grossProfit)}
          subtitle={`Marża: ${formatNumber(kpis.grossMargin, 1)}%`}
          icon={kpis.grossProfit >= 0 ? TrendingUp : TrendingDown}
        />
        <KpiCard
          title="Do zapłaty"
          value={formatCompactCurrency(kpis.outstandingAmount)}
          subtitle={`${kpis.outstandingCount} niezapłaconych`}
          icon={AlertCircle}
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 [&>*]:mx-auto [&>*]:w-full [&>*]:max-w-2xl xl:[&>*]:max-w-none">
        {!selectedProjectType && !showUnassignedRevenues && (
          <ChartCard title="Przychody vs Koszty" description="Miesięczne zestawienie 2025">
            <ChartContainer config={chartConfig} className="h-[300px]">
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="month" className="text-xs" />
                <YAxis
                  tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                  className="text-xs"
                />
                <ChartTooltip
                  content={<ChartTooltipContent />}
                  formatter={(value: number) => formatCurrency(value)}
                />
                <ChartLegend content={<ChartLegendContent />} />
                <Bar dataKey="przychód" fill="var(--color-przychód)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="koszty" fill="var(--color-koszty)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartContainer>
          </ChartCard>
        )}

        <ChartCard
          title={selectedProjectType
            ? `Przychód wg typu projektu: ${selectedProjectType}`
            : showUnassignedRevenues
            ? "Nieprzypisane przychody"
            : "Przychód wg typu projektu"}
          description={selectedProjectType
            ? `${drilldownProjects.length} projektów w kategorii`
            : showUnassignedRevenues
            ? `${unassignedRevenues.length} faktur bez przypisanego projektu`
            : "Top 8 kategorii"}
          className={cn(
            "transition-all duration-300",
            (selectedProjectType || showUnassignedRevenues) && "xl:col-span-2"
          )}
          isExpanded={!!selectedProjectType || showUnassignedRevenues}
          onCollapse={handleCloseDrilldown}
        >
          <div className={cn(
            (selectedProjectType || showUnassignedRevenues) && "flex flex-col gap-6"
          )}>
            <div className="flex flex-col gap-4">
              <ChartContainer config={{}} className={cn(
                "h-[300px]",
                (selectedProjectType || showUnassignedRevenues) && "mx-auto w-full max-w-xl"
              )}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="45%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                    onClick={handlePieSliceClick}
                    cursor="pointer"
                  >
                    {pieChartData.map((entry, index) => {
                      const isSelected = selectedProjectType === entry.name ||
                        (showUnassignedRevenues && entry.name === "Nieprzypisane");
                      return (
                        <Cell
                          key={`cell-${index}`}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                          stroke={isSelected ? "hsl(0, 0%, 20%)" : undefined}
                          strokeWidth={isSelected ? 3 : 0}
                          style={{
                            filter: isSelected ? "brightness(1.1)" : undefined,
                            transform: isSelected ? "scale(1.05)" : undefined,
                            transformOrigin: "center",
                          }}
                        />
                      );
                    })}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                </PieChart>
              </ChartContainer>

              {!selectedProjectType && !showUnassignedRevenues && unassignedRevenues.length > 0 && (
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowUnassignedRevenues(true);
                      setDrilldownPage(1);
                    }}
                  >
                    Pokaż nieprzypisane przychody ({unassignedRevenues.length})
                  </Button>
                </div>
              )}
            </div>

            {selectedProjectType && (
              <ProjectDrilldownTable
                projects={paginatedProjects}
                currentPage={drilldownPage}
                totalPages={totalPages}
                onPageChange={setDrilldownPage}
                costs={filteredCosts}
                revenues={filteredRevenues}
                mpkAllocations={mpkAllocations}
              />
            )}

            {showUnassignedRevenues && (
              <UnassignedRevenuesTable
                revenues={paginatedUnassignedRevenues}
                currentPage={drilldownPage}
                totalPages={unassignedTotalPages}
                onPageChange={setDrilldownPage}
              />
            )}
          </div>
        </ChartCard>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 [&>*]:mx-auto [&>*]:w-full [&>*]:max-w-2xl xl:[&>*]:max-w-none">
        <ChartCard title="Status płatności" description="Faktury wg statusu">
          <ChartContainer config={{}} className="h-[250px]">
            <PieChart>
              <Pie
                data={paymentStatus}
                cx="50%"
                cy="45%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
                nameKey="name"
              >
                {paymentStatus.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ChartContainer>
        </ChartCard>

        <ChartCard title="Trend przychodów" description="Narastająco 2025" className="xl:col-span-2">
          <ChartContainer config={chartConfig} className="h-[250px]">
            <AreaChart data={revenueTrend}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="month" className="text-xs" />
              <YAxis
                tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
                className="text-xs"
              />
              <ChartTooltip
                content={<ChartTooltipContent />}
                formatter={(value: number) => formatCurrency(value)}
              />
              <Area
                type="monotone"
                dataKey="skumulowany"
                stroke="var(--color-skumulowany)"
                fill="var(--color-skumulowany)"
                fillOpacity={0.2}
              />
            </AreaChart>
          </ChartContainer>
        </ChartCard>
      </div>
    </div>
  );
}
